import { useQuery } from "@tanstack/react-query";
import { Card, Spinner } from "@nutrayan/ui";
import { formatCurrency, formatIndianDate } from "@nutrayan/utils";
import { apiClient } from "@/lib/api-client";

// Contract: GET /admin/api/v1/analytics/executive-summary → DashboardSummary
interface DashboardSummary {
  from?: string;
  to?: string;
  totalGrossPaise?: number;
  totalRefundsPaise?: number;
  totalPayments?: number;
  totalUsers?: number;
  totalAtcEvents?: number;
  totalCheckouts?: number;
  totalPurchases?: number;
  dailyRevenue?: Array<{
    date?: string;
    grossPaise?: number;
    refundsPaise?: number;
    netPaise?: number;
    paymentCount?: number;
  }>;
}

// Contract: GET /admin/api/v1/orders → OrderSummary[]
interface OrderSummary {
  id: number;
  orderNumber?: string;
  status?: string;
  paymentMethod?: string;
  grandTotalPaise?: number;
  itemCount?: number;
  placedAt?: string;
}

export default function DashboardPage() {
  const summary = useQuery({
    queryKey: ["admin", "summary"],
    queryFn: () => apiClient.request<DashboardSummary>("/admin/api/v1/analytics/executive-summary"),
    retry: 1,
  });
  const recentOrders = useQuery({
    queryKey: ["admin", "recent-orders"],
    queryFn: () => apiClient.request<OrderSummary[]>("/admin/api/v1/orders?page=0&size=5"),
    retry: 1,
  });

  const s = summary.data;
  const aovPaise =
    s?.totalGrossPaise && s?.totalPurchases ? Math.round(s.totalGrossPaise / s.totalPurchases) : 0;

  return (
    <div className="space-y-8">
      <header>
        <h1 className="font-display text-display-xl text-charcoal">Dashboard</h1>
        <p className="text-body text-ink">
          Top-level KPIs{s?.from ? ` (${s.from} → ${s.to ?? "today"})` : ""}.
        </p>
      </header>

      {summary.isPending ? (
        <div className="flex items-center justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : summary.isError ? (
        <Card>
          <p className="text-body text-terracotta">
            Couldn&apos;t load the executive summary. Backend may not have data yet.
          </p>
        </Card>
      ) : (
        <section className="grid gap-4 md:grid-cols-4">
          <Card>
            <p className="text-caption text-muted">Purchases</p>
            <p className="font-display text-display-lg text-charcoal mt-1">
              {s?.totalPurchases ?? 0}
            </p>
          </Card>
          <Card>
            <p className="text-caption text-muted">Gross revenue</p>
            <p className="font-display text-display-lg text-charcoal mt-1">
              {formatCurrency(s?.totalGrossPaise ?? 0)}
            </p>
          </Card>
          <Card>
            <p className="text-caption text-muted">AOV</p>
            <p className="font-display text-display-lg text-charcoal mt-1">
              {formatCurrency(aovPaise)}
            </p>
          </Card>
          <Card>
            <p className="text-caption text-muted">Checkouts started</p>
            <p className="font-display text-display-lg text-charcoal mt-1">
              {s?.totalCheckouts ?? 0}
            </p>
          </Card>
        </section>
      )}

      <section>
        <h2 className="font-display text-display-lg text-charcoal">Recent orders</h2>
        {recentOrders.isPending ? (
          <Spinner className="mt-3" />
        ) : (recentOrders.data ?? []).length ? (
          <Card className="mt-3 p-0">
            <table className="text-body-sm w-full">
              <thead className="border-charcoal/10 text-caption text-muted border-b text-left">
                <tr>
                  <th className="px-4 py-2">Order</th>
                  <th className="px-4 py-2">Placed</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2">Payment</th>
                  <th className="px-4 py-2 text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {(recentOrders.data ?? []).map((o) => (
                  <tr key={o.id} className="border-charcoal/5 border-b">
                    <td className="text-charcoal px-4 py-2 font-mono">{o.orderNumber ?? o.id}</td>
                    <td className="text-ink px-4 py-2">
                      {o.placedAt ? formatIndianDate(o.placedAt) : "—"}
                    </td>
                    <td className="text-ink px-4 py-2">{o.status ?? "—"}</td>
                    <td className="text-ink px-4 py-2">{o.paymentMethod ?? "—"}</td>
                    <td className="price text-charcoal px-4 py-2 text-right">
                      {formatCurrency(o.grandTotalPaise ?? 0)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        ) : (
          <Card className="mt-3">
            <p className="text-body text-ink">No recent orders.</p>
          </Card>
        )}
      </section>
    </div>
  );
}
